import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Layout } from '@/components/layout'
import { useAuth } from '@/lib/hooks/useAuth'
import { useAuthStore } from '@/lib/stores/authStore'
import { useToast } from '@/lib/hooks/useToast'
import { TwoFactorInput } from '@/components/auth/TwoFactorInput'
import { BackupCodeInput } from '@/components/auth/BackupCodeInput'
import { MicrosoftSignInButton } from '@/components/auth/MicrosoftSignInButton'
import { Separator } from '@/components/ui/separator'
import { twoFactorService, type TwoFactorSetupResponse } from '@/lib/services/two-factor'
import { ShieldCheck, QrCode, AlertCircle } from 'lucide-react'

export function LoginPage() {
  const { t } = useTranslation()
  const { login, isLoading, isAuthenticated, requires2faSetup, setRequires2FASetup } = useAuth()
  const { toast } = useToast()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })
  const [requires2FA, setRequires2FA] = useState(false)
  const [twoFactorSetupData, setTwoFactorSetupData] = useState<TwoFactorSetupResponse | null>(null)
  const [twoFactorSetupStep, setTwoFactorSetupStep] = useState<'scan' | 'verify' | 'backup'>('scan')
  const [setupVerificationCode, setSetupVerificationCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [showBackupCode, setShowBackupCode] = useState(false)
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null)
  const [provisioningError, setProvisioningError] = useState<{ isNewUser: boolean; message: string } | null>(null)

  // Current year for footer
  const currentYear = new Date().getFullYear()

  // Redirect if user is already authenticated
  useEffect(() => {
    if (isAuthenticated && !requires2faSetup) {
      window.location.href = '/'
    }
  }, [isAuthenticated, requires2faSetup])

  // Check for provisioning error from Azure callback
  useEffect(() => {
    const storedError = sessionStorage.getItem('provisioningError')
    if (storedError) {
      try {
        const error = JSON.parse(storedError)
        const translationKey = error.isNewUser
          ? 'auth.userNotProvisionedNewUser'
          : 'auth.userNotProvisionedExistingUser'
        const translatedMessage = t(translationKey) as string
        setProvisioningError({
          isNewUser: error.isNewUser,
          message: translatedMessage,
        })
        // Clear from sessionStorage so it doesn't persist
        sessionStorage.removeItem('provisioningError')
      } catch (e) {
        console.error('Failed to parse provisioning error:', e)
        sessionStorage.removeItem('provisioningError')
      }
    }
  }, [t])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      // Attempt login - this will handle both 2FA and non-2FA cases
      await login(formData.email, formData.password)
      toast({
        title: t('auth.loginSuccess', 'Login Successful'),
        description: t('auth.loginSuccessDescription', 'Welcome back!'),
        variant: 'success',
      })
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('auth.authenticationFailed', 'Authentication failed')

      // Check if rate limit exceeded
      if (errorMessage === 'auth.rateLimitExceeded') {
        toast({
          title: t('auth.rateLimitExceeded', 'Too Many Login Attempts'),
          description: t('auth.rateLimitExceededDescription', 'For your security, login attempts are limited. Please wait a few minutes and try again.'),
          variant: 'destructive',
        })
        return
      }

      // Check if 2FA setup is required
      if (errorMessage === 'auth.twoFactorSetupRequired') {
        // Start 2FA setup flow
        try {
          const setupData = await twoFactorService.setup()
          setTwoFactorSetupData(setupData)
          setRequires2FASetup(true)
          setTwoFactorSetupStep('scan')
        } catch (setupError) {
          console.error('Failed to start 2FA setup:', setupError)
          toast({
            title: t('auth.loginError', 'Login Failed'),
            description: t('auth.twoFactorSetupError', 'Failed to initialize 2FA setup'),
            variant: 'destructive',
          })
        }
        return
      }

      // Check if 2FA is required
      if (errorMessage === 'auth.twoFactorRequired') {
        setRequires2FA(true)
        return
      }

      // Check if user is not provisioned
      if (errorMessage === 'auth.userNotProvisioned') {
        const details = (err as unknown as Record<string, unknown>)?.details as Record<string, unknown> || {}
        const isNewUser = details.is_new_user === true
        const translationKey = isNewUser
          ? 'auth.userNotProvisionedNewUser'
          : 'auth.userNotProvisionedExistingUser'
        setProvisioningError({
          isNewUser,
          message: t(translationKey, errorMessage),
        })
        return
      }

      // Check if the error message is a translation key (starts with namespace)
      const translatedMessage = errorMessage.startsWith('auth.')
        ? t(errorMessage, errorMessage) // Fallback to key if translation not found
        : errorMessage

      toast({
        title: t('auth.loginError', 'Login Failed'),
        description: translatedMessage,
        variant: 'destructive',
      })
    }
  }

  const handle2FASubmit = async (token: string) => {
    setTwoFactorError(null)
    try {
      // Login with 2FA token
      await login(formData.email, formData.password, token)
      toast({
        title: t('auth.loginSuccess', 'Login Successful'),
        description: t('auth.loginSuccessDescription', 'Welcome back!'),
        variant: 'success',
      })
      // Redirect to home
      window.location.href = '/'
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('auth.authenticationFailed', 'Authentication failed')

      // Check if rate limit exceeded
      if (errorMessage === 'auth.rateLimitExceeded') {
        setTwoFactorError(t('auth.rateLimitExceededDescription', 'For your security, login attempts are limited. Please wait a few minutes and try again.'))
        return
      }

      const translatedMessage = errorMessage.startsWith('auth.')
        ? t(errorMessage, errorMessage)
        : errorMessage
      setTwoFactorError(translatedMessage)
    }
  }

  const handleSetupVerify = async () => {
    if (!setupVerificationCode || setupVerificationCode.length !== 6) {
      toast({
        title: t('algemeen.fout', 'Error'),
        description: t('auth.invalidCode', 'Please enter a valid 6-digit code'),
        variant: 'destructive',
      })
      return
    }

    try {
      const result = await twoFactorService.enable(setupVerificationCode)
      setBackupCodes(result.backupCodes)

      // If the backend returned user data (setup token flow), store it
      // The backend also set a full access token cookie
      if (result.user) {
        const { setUser, setAuthenticated } = useAuthStore.getState()
        setUser(result.user)
        setAuthenticated(true)
      }

      setTwoFactorSetupStep('backup')
      toast({
        title: t('algemeen.succes', 'Success'),
        description: t('auth.twoFactorEnabled', '2FA has been enabled successfully'),
        variant: 'success',
      })
    } catch (error) {
      console.error('Failed to verify 2FA setup:', error)
      toast({
        title: t('algemeen.fout', 'Error'),
        description: t('auth.invalidVerificationCode', 'Invalid verification code'),
        variant: 'destructive',
      })
    }
  }

  const handleSetupComplete = async () => {
    // User is already authenticated from handleSetupVerify - just redirect
    toast({
      title: t('auth.loginSuccess', 'Login Successful'),
      description: t('auth.loginSuccessDescription', 'Welcome back!'),
      variant: 'success',
    })
    window.location.href = '/'
  }

  const downloadBackupCodes = () => {
    const content = backupCodes.join('\n')
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'crisis-buddy-backup-codes.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'))
    toast({
      title: t('algemeen.succes', 'Success'),
      description: t('auth.backupCodesCopied', 'Backup codes copied to clipboard'),
      variant: 'success',
    })
  }

  return (
    <Layout showNavigation={false}>
      {/* Fill available height from Layout and center content. Avoid forcing viewport height here. */}
      <div className="min-h-full flex flex-col px-4 sm:px-6 lg:px-8">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Logo Section */}
            <div className="hidden lg:flex flex-col items-center justify-center">
              <img
                src="/tenant_logo.png"
                alt="Tenant logo"
                className="w-80 object-contain"
              />
            </div>

            {/* Right: Login Section */}
            <div className="w-full space-y-8">
              {/* Header Section */}
              <div className="text-center lg:text-left space-y-4">
                <div className="flex lg:hidden justify-center mb-4">
                  <img
                    src="/tenant_logo.png"
                    alt="Tenant logo"
                    className="w-40 object-contain"
                  />
                </div>
              </div>

              {/* Login Card */}
              <Card variant="elevated" className="animate-fade-in glass-card dark:glass-card">
                <CardHeader className="text-center space-y-3">
                  <CardTitle className="text-2xl">
                    {requires2faSetup
                      ? t('auth.setupTwoFactor', 'Set up Two-Factor Authentication')
                      : requires2FA
                        ? t('auth.twoFactorTitle', 'Two-Factor Authentication')
                        : t('auth.inloggenTitel')}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {requires2faSetup
                      ? t('auth.setupRequired', 'Two-factor authentication is required for your account')
                      : requires2FA
                        ? t('auth.twoFactorDescription', 'Enter your authentication code to continue')
                        : 'Krijg toegang tot de AI Crisiscommunicatie Buddy'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {requires2faSetup ? (
                    // 2FA Setup Flow
                    <div className="space-y-4">
                      {twoFactorSetupStep === 'scan' && twoFactorSetupData && (
                        <>
                          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 p-4 rounded-lg flex items-start gap-3">
                            <ShieldCheck className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-blue-900 dark:text-blue-100">
                              <p className="font-semibold mb-1">
                                {t('auth.securityRequired', 'Security Enhancement Required')}
                              </p>
                              <p className="opacity-90">
                                {t(
                                  'auth.setupExplanation',
                                  'For your security, two-factor authentication is now required. Please scan the QR code with your authenticator app.'
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-center p-4 bg-white dark:bg-gray-900 rounded-lg">
                            <img src={twoFactorSetupData.qrCode} alt="QR Code" className="w-64 h-64" />
                          </div>
                          <div className="bg-muted p-3 rounded-md space-y-2">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <QrCode className="h-4 w-4" />
                              <span>{t('auth.manualEntry', 'Manual entry')}:</span>
                            </div>
                            <p className="font-mono text-xs break-all">{twoFactorSetupData.secret}</p>
                          </div>
                          <Button className="w-full" onClick={() => setTwoFactorSetupStep('verify')}>
                            {t('auth.continue', 'Continue')}
                          </Button>
                        </>
                      )}
                      {twoFactorSetupStep === 'verify' && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="setup-verification-code">
                              {t('auth.verificationCode', 'Verification Code')}
                            </Label>
                            <Input
                              id="setup-verification-code"
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              value={setupVerificationCode}
                              onChange={e => setSetupVerificationCode(e.target.value.replace(/\D/g, ''))}
                              placeholder="000000"
                              className="text-center text-lg font-mono"
                              disabled={isLoading}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              className="flex-1"
                              onClick={() => setTwoFactorSetupStep('scan')}
                              disabled={isLoading}
                            >
                              {t('auth.back', 'Back')}
                            </Button>
                            <Button
                              className="flex-1"
                              onClick={handleSetupVerify}
                              disabled={isLoading || setupVerificationCode.length !== 6}
                            >
                              {isLoading ? t('algemeen.laden', 'Loading...') : t('auth.verify', 'Verify')}
                            </Button>
                          </div>
                        </>
                      )}
                      {twoFactorSetupStep === 'backup' && (
                        <>
                          <div className="bg-muted p-4 rounded-lg font-mono text-sm space-y-2">
                            {backupCodes.map((code, index) => (
                              <div key={index} className="text-center">
                                {code}
                              </div>
                            ))}
                          </div>
                          <div className="bg-destructive/10 border border-destructive/20 text-destructive p-3 rounded-md flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <p className="text-sm">
                              {t(
                                'auth.backupCodesWarning',
                                'These codes will only be shown once. Make sure to save them!'
                              )}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" className="flex-1" onClick={copyBackupCodes}>
                              {t('auth.copy', 'Copy')}
                            </Button>
                            <Button variant="outline" className="flex-1" onClick={downloadBackupCodes}>
                              {t('auth.download', 'Download')}
                            </Button>
                          </div>
                          <Button className="w-full" onClick={handleSetupComplete}>
                            {t('auth.completeSetup', 'Complete Setup & Login')}
                          </Button>
                        </>
                      )}
                    </div>
                  ) : !requires2FA ? (
                    <>
                      {provisioningError ? (
                        // Show provisioning message
                        <div className={`rounded-lg p-4 flex flex-col justify-center ${provisioningError.isNewUser ? 'bg-blue-50 dark:bg-blue-300' : 'bg-yellow-200 dark:bg-yellow-200'}`}>
                          <div className={`flex gap-3 ${provisioningError.isNewUser ? 'text-blue-900 dark:text-blue-900' : 'text-yellow-900 dark:text-yellow-900'}`}>
                            {provisioningError.isNewUser ? (
                              <div className="flex-shrink-0 text-lg">✓</div>
                            ) : (
                              <div className="flex-shrink-0 text-lg">⚠️</div>
                            )}
                            <div>
                              <p className="font-semibold mb-1">
                                {provisioningError.isNewUser
                                  ? t('auth.userNotProvisioned', 'Account Pending Activation')
                                  : t('auth.userNotProvisioned', 'Account Not Activated')}
                              </p>
                              <p className="text-sm opacity-90">{provisioningError.message}</p>
                            </div>
                          </div>
                          <button
                            className="mt-4 text-sm font-medium underline hover:no-underline"
                            onClick={() => {
                              setProvisioningError(null)
                              setFormData({ email: '', password: '' })
                            }}
                          >
                            {t('auth.inloggenKnop', 'Try again with different credentials')}
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* Microsoft SSO Sign-In */}
                          <div className="space-y-4">
                            <MicrosoftSignInButton redirectPath="/" fullWidth />
                          </div>

                          {/* Divider */}
                          <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                              <Separator className="w-full" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                              <span className="bg-card px-2 text-muted-foreground">
                                {t('auth.orContinueWith', 'Of ga door met')}
                              </span>
                            </div>
                          </div>

                          {/* Email/Password Form */}
                          <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="email">{t('auth.email', 'Email')}</Label>
                              <Input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder={t('auth.emailPlaceholder', 'Enter your email')}
                                required
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="password">{t('auth.wachtwoord', 'Password')}</Label>
                              <Input
                                id="password"
                                name="password"
                                type="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder={t('auth.wachtwoordPlaceholder', 'Enter your password')}
                                required
                              />
                            </div>

                            <Button
                              className="w-full h-12 text-base font-semibold"
                              variant="gradient"
                              type="submit"
                              disabled={isLoading}
                            >
                              {isLoading ? t('algemeen.laden', 'Loading...') : t('auth.inloggenKnop')}
                            </Button>
                          </form>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="space-y-4">
                      {!showBackupCode ? (
                        <TwoFactorInput
                          onSubmit={handle2FASubmit}
                          onUseBackupCode={() => setShowBackupCode(true)}
                          isLoading={isLoading}
                          {...(twoFactorError && { error: twoFactorError })}
                        />
                      ) : (
                        <BackupCodeInput
                          onSubmit={handle2FASubmit}
                          onBackToTOTP={() => {
                            setShowBackupCode(false)
                            setTwoFactorError(null)
                          }}
                          isLoading={isLoading}
                          {...(twoFactorError && { error: twoFactorError })}
                        />
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Footer - Full Width */}
        <div className="mt-8 border-t border-border pt-8">
          <div className="flex flex-col items-center space-y-4">
            {/* Links Row */}
            <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
              <a href="/privacy-policy" className="hover:text-foreground transition-colors">
                Privacybeleid
              </a>
              <a href="/security-policy" className="hover:text-foreground transition-colors">
                Beveiligingsbeleid
              </a>
              <a href="#" className="hover:text-foreground transition-colors">
                {t('navigatie.help')}
              </a>
            </div>

            {/* Logo and Copyright Row */}
            <div className="flex items-center justify-center space-x-4">
              <a
                href="https://techletes.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
                title="Made by TECHLETES"
              >
                <img
                  src="/techletes_logo.svg"
                  alt="TECHLETES"
                  className="h-8 object-contain"
                />
              </a>

              <p className="text-xs text-muted-foreground">
                © {currentYear} TECHLETES. Alle rechten voorbehouden.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}
